/**
 * DaVinci API Helper Functions
 *
 * Utility functions for DaVinci API integration:
 * - Date formatting (JavaScript Date ↔ DaVinci .NET format)
 * - Service mapping (offer parameters → DaVinci booking service)
 * - Participant creation
 * - Default client creation
 */

import type { DaVinciBookingService, DaVinciParticipant, DaVinciClient } from '@/types/schemas';
import type { RoomCombination, MappedOffer } from '@/services/mappers/offerMapper';
import type { CheckoutFormData } from '@/types/schemas/checkout';
import type { Traveler } from '@/types/Traveler';
import type { SscInsurance } from '@/types/schemas/ssc';
import { calculateInsuranceAmount } from '@/types/schemas/ssc';
import type { PpsService } from '@/types/schemas/pps';
import { CHECKOUT_CONFIG } from '@/constants/checkout/config';
import { getNationalityName } from '@/constants/checkout/nationalities';
import { toUtcMidnight } from '@/utils/formatting/dateFormatter';
import { parseGermanDateUtc } from '@/utils/formatting/dateParser';
import { effectiveHotelClass } from '@/utils/checkout/classification';
import { DAVINCI_CONFIG } from './config';
import type { DeepReadonly } from 'vue';

/**
 * Default Passport object. Used by the availability-phase participant builder
 * (createParticipantsFromInfo), where real traveler identities — and therefore
 * nationalities — are not yet known. The final booking
 * (createParticipantsFromTravelers) sends each traveler's own nationality.
 */
const DEFAULT_PASSPORT = {
  Nationality: DAVINCI_CONFIG.DEFAULT_NATIONALITY,
  NationalityCode: DAVINCI_CONFIG.DEFAULT_NATIONALITY_CODE,
} as const;

/**
 * Single source of truth mapping a TravelType prefix to its AgencyNumber.
 * VR… → 1, VV… → 2. Anything else falls back to DAVINCI_CONFIG.AGENCY_NUMBER.
 */
const AGENCY_NUMBER_BY_TRAVEL_TYPE_PREFIX: Readonly<Record<string, number>> = {
  VR: 1,
  VV: 2,
};

function travelTypePrefix(travelType: string | null | undefined): string {
  return (travelType ?? '').trim().toUpperCase().slice(0, 2);
}

/**
 * Derive the AgencyNumber that must accompany a given TravelType.
 *
 * The first checkout BA is sent with a blank TravelType; DaVinci answers with the
 * correct one, but the AgencyNumber in that response is not usable, so we map it
 * ourselves from the TravelType prefix for every subsequent request.
 *
 * @param travelType - TravelType string from the first BA response (may be null/blank).
 * @returns Agency number to send on follow-up BA / B / BQ / BC requests.
 */
export function deriveAgencyNumberFromTravelType(travelType: string | null | undefined): number {
  return (
    AGENCY_NUMBER_BY_TRAVEL_TYPE_PREFIX[travelTypePrefix(travelType)] ??
    DAVINCI_CONFIG.AGENCY_NUMBER
  );
}

/**
 * Whether a TravelType prefix maps to a known AgencyNumber (VR/VV). When false but
 * a TravelType was returned, the derived AgencyNumber came from the fallback.
 *
 * @param travelType - TravelType string from the first BA response (may be null/blank).
 */
export function isKnownTravelTypePrefix(travelType: string | null | undefined): boolean {
  return travelTypePrefix(travelType) in AGENCY_NUMBER_BY_TRAVEL_TYPE_PREFIX;
}

/**
 * Build a DaVinci Passport from a traveler's selected nationality code.
 *
 * @param nationalityCode - ISO 3166-1 alpha-2 code from the form (e.g. 'DE').
 * @returns Passport with the ISO code and its German country name. Falls back to
 *   DEFAULT_PASSPORT if no nationality is set (should not happen — the field is
 *   required — but keeps the booking payload valid defensively).
 */
function buildPassport(nationalityCode?: string): {
  Nationality: string;
  NationalityCode: string;
} {
  if (!nationalityCode) return DEFAULT_PASSPORT;
  return {
    Nationality: getNationalityName(nationalityCode),
    NationalityCode: nationalityCode,
  };
}

/**
 * Format JavaScript Date to DaVinci .NET format
 *
 * @param date - JavaScript Date object
 * @returns .NET date string (e.g., "/Date(1758900284000)/")
 *
 * @example
 * formatDateForDaVinci(new Date('2024-07-01'))
 * // Returns: "/Date(1719792000000)/"
 */
export function formatDateForDaVinci(date: Date): string {
  return `/Date(${date.getTime()})/`;
}

/**
 * Matches the .NET date wire format, with or without a trailing UTC offset.
 *
 * Responses from the gateway carry the offset (`/Date(1790287200000+0200)/`),
 * while everything this app *sends* omits it. The epoch is always absolute —
 * the offset only says which calendar day that instant falls on locally.
 */
const DAVINCI_DATE_PATTERN = /\/Date\((-?\d+)(?:([+-])(\d{2})(\d{2}))?\)\//;

/**
 * Parse DaVinci .NET date format to JavaScript Date
 *
 * Accepts both the bare form and the offset-carrying form returned by the API.
 * The offset is ignored here because the epoch is already absolute; use
 * {@link tryParseDaVinciDate} when you need the offset to render a calendar date.
 *
 * @param dateString - .NET date string (e.g., "/Date(1758900284000)/")
 * @returns JavaScript Date object
 * @throws {Error} If date format is invalid
 *
 * @example
 * parseDaVinciDate("/Date(1719792000000)/")
 * // Returns: Date object for 2024-07-01
 *
 * @example
 * parseDaVinciDate("/Date(1790287200000+0200)/")
 * // Returns: Date object for the same instant (offset ignored)
 */
export function parseDaVinciDate(dateString: string): Date {
  const match = dateString.match(DAVINCI_DATE_PATTERN);
  if (!match) {
    throw new Error(`Invalid DaVinci date format: ${dateString}`);
  }
  return new Date(parseInt(match[1], 10));
}

/**
 * Offset-aware, non-throwing counterpart to {@link parseDaVinciDate}.
 *
 * Returns the absolute instant together with the UTC offset embedded in the
 * string. That offset is load-bearing for *display*: DaVinci sends due dates as
 * local midnight, so `/Date(1790287200000+0200)/` is 2026-09-24T22:00Z — reading
 * its UTC fields would show 24.09. instead of the correct 25.09. Pass
 * `offsetMinutes` on to `formatDayMonthYear` to get the calendar day right.
 *
 * Use this for parsing API payloads, where a missing or malformed date must not
 * blow up the surrounding extraction.
 *
 * @param value - Candidate value of unknown type
 * @returns The instant plus its offset in minutes, or null if unparseable
 *
 * @example
 * tryParseDaVinciDate("/Date(1790287200000+0200)/")
 * // Returns: { date: Date(2026-09-24T22:00:00Z), offsetMinutes: 120 }
 */
export function tryParseDaVinciDate(value: unknown): { date: Date; offsetMinutes: number } | null {
  if (typeof value !== 'string') return null;

  const match = value.match(DAVINCI_DATE_PATTERN);
  if (!match) return null;

  const ms = Number.parseInt(match[1], 10);
  if (!Number.isFinite(ms)) return null;

  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;

  let offsetMinutes = 0;
  if (match[2]) {
    const magnitude = Number.parseInt(match[3], 10) * 60 + Number.parseInt(match[4], 10);
    offsetMinutes = match[2] === '-' ? -magnitude : magnitude;
  }

  return { date, offsetMinutes };
}

/**
 * Calculate birth date from age
 *
 * Subtracts the specified age from today's date to get the birth date.
 * Default ages: adults = 35 years, children = 10 years (if not specified)
 *
 * @param age - Age of the person in years
 * @returns Birth date in DaVinci .NET format (/Date(milliseconds)/)
 *
 * @example
 * calculateBirthDate(1)
 * // Returns: "/Date(...)/" (1 year before today)
 *
 * @example
 * calculateBirthDate(35)
 * // Returns: "/Date(...)/" (35 years before today)
 */
export function calculateBirthDate(age: number): string {
  const now = new Date();
  const birthDate = new Date(Date.UTC(now.getFullYear() - age, now.getMonth(), now.getDate()));
  return formatDateForDaVinci(birthDate);
}

/**
 * Create default client object (empty template for BA action)
 *
 * @returns Empty DaVinci client object
 *
 * @example
 * createDefaultClient()
 * // Returns: {
 * //   EMail: null,
 * //   Name: '',
 * //   Surname: '',
 * //   Address: { Street: null, ZipCode: null, City: null, Country: null }
 * // }
 */
export function createDefaultClient(): DaVinciClient {
  return {
    EMail: null,
    Telephone: null,
    Name: '',
    Surname: '',
    Address: {
      Street: null,
      ZipCode: null,
      City: null,
      Country: null,
    },
  };
}

/**
 * Create participant assignment string for specific passenger IDs in DaVinci range format.
 *
 * DaVinci uses range semantics: `"1-3"` means participants 1 through 3, not three
 * individual participants. Passenger IDs must be consecutive — non-consecutive IDs
 * cannot be expressed as a single range and would silently change meaning, so we
 * fail fast.
 *
 * @param passengerIds - Array of consecutive passenger IDs (1-based)
 * @returns Range-formatted participant string (e.g., "1-3" or "3")
 * @throws {Error} If passengerIds is empty or non-consecutive
 *
 * @example
 * createParticipantAssignmentForIds([1, 2, 3])
 * // Returns: "1-3"
 *
 * @example
 * createParticipantAssignmentForIds([3])
 * // Returns: "3"
 */
export function createParticipantAssignmentForIds(passengerIds: number[]): string {
  if (passengerIds.length === 0) {
    throw new Error('createParticipantAssignmentForIds: passengerIds must not be empty');
  }
  // Sort defensively — Player API does not document ordering of PassengerIds.PassengerId
  const sorted = [...passengerIds].sort((a, b) => a - b);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const isConsecutive = sorted.every((id, i) => id === first + i);
  if (!isConsecutive) {
    throw new Error(
      `createParticipantAssignmentForIds: passengerIds must be consecutive, got [${passengerIds.join(',')}]`
    );
  }
  return sorted.length === 1 ? String(first) : `${first}-${last}`;
}

/**
 * Create participants array from PassengerInfo objects.
 *
 * Used in the initial multi-room availability check (BA #1), before data entry, where we
 * only know each passenger's hotel classification and age (from the pax URL param) — not
 * their birth date. Dispatch is on `hotelClass` (the hotel's Adult/Child/Infant label),
 * falling back to the binary `type` for plain/bookmarked URLs that carry no classification:
 *
 *   - infant → Salutation "B" + BirthDate (derived from age)
 *   - child  → Salutation "K" + BirthDate (derived from age)
 *   - adult  → Salutation "H" + BirthDate (an under-18 hotel-adult, e.g. age 16, still uses
 *              "H" as the pre-form placeholder, but BirthDate(16) so the hotel sees a youth)
 *
 * Everyone carries BirthDate here (derived from their age via calculateBirthDate), so DaVinci
 * computes the exact age at the travel date on every check. The child/infant marker is carried
 * by the Salutation ("K"/"B"), not by the age representation — DaVinci classifies them correctly
 * from the salutation (live-confirmed, case C: BirthDate is accepted for child/infant on a
 * hotel-only BA).
 *
 * NOTE (RC-933, deferred): a prior decision sent infants/children as AgeInYears here to avoid the
 * insurance child-cap (933) miscounting them as adults. Auxiliaries/insurance are currently
 * disabled (HIDE_AUXILIARIES_STEP), so no request carries insurance and BirthDate is sent for all.
 * When insurance is re-enabled, re-test RC-933 (see davinci-insurance-probe.js); if BirthDate then
 * trips 933, reintroduce an AgeInYears fallback for child/infant on insurance-bearing requests.
 *
 * Default ages: adults = 35 years, children = 10 years, infants = 1 year (if not specified).
 */
export function createParticipantsFromInfo(
  passengers: Array<{
    id?: number;
    type: 'adult' | 'child';
    age?: number;
    hotelClass?: 'adult' | 'child' | 'infant';
  }>
): DaVinciParticipant[] {
  return passengers.map((passenger) => {
    // Prefer the hotel classification; fall back to the binary type for plain URLs.
    const hotelClass = effectiveHotelClass(passenger);

    if (hotelClass === 'infant') {
      return {
        Salutation: 'B',
        Gender: 0,
        Name: 'NN/NN',
        DoNotAutoCreateClient: true,
        Age: { BirthDate: calculateBirthDate(passenger.age ?? 1) },
        Passport: DEFAULT_PASSPORT,
      };
    }
    if (hotelClass === 'child') {
      return {
        Salutation: 'K',
        Gender: 0,
        Name: 'NN/NN',
        DoNotAutoCreateClient: true,
        Age: { BirthDate: calculateBirthDate(passenger.age ?? 10) },
        Passport: DEFAULT_PASSPORT,
      };
    }
    // adult (incl. under-18 hotel-adult): real age → BirthDate, defaulting to 35.
    return {
      Salutation: 'H',
      Gender: 0,
      Name: 'NN/NN',
      DoNotAutoCreateClient: true,
      Age: { BirthDate: calculateBirthDate(passenger.age ?? 35) },
      Passport: DEFAULT_PASSPORT,
    };
  });
}

/**
 * Create DaVinci services from room combination
 *
 * Converts a RoomCombination (multiple rooms) into an array of DaVinci booking services.
 * Each service represents one room in the multi-room booking.
 * Services are assigned to their respective passengers via ParticipantAssignment.
 *
 * @param combination - RoomCombination object (multiple products/rooms)
 * @param productCode - Product Code (GIATA ID) for the destination/accommodation
 * @returns Array of DaVinciBookingService objects (one per room)
 *
 * @example
 * // Multi-room combination with 2 rooms, 3 passengers total
 * const combination: RoomCombination = {
 *   products: [
 *     { roomCode: 'D03', boardCode: 'H', passengerIds: [1, 2], ... },
 *     { roomCode: 'D01', boardCode: 'H', passengerIds: [3], ... }
 *   ],
 *   startDate: '2025-11-15',
 *   endDate: '2025-11-22',
 *   boardCode: 'H',
 *   ...
 * }
 *
 * const services = createMultiRoomServices(combination, 'HERIRINI119');
 * // Returns: [
 * //   {
 * //     Requirement: 'H',
 * //     Service: 'HERIRINI119',
 * //     Accommodation: 'D03H',
 * //     Amount: 1,
 * //     From: '/Date(...)/',
 * //     Till: '/Date(...)/',
 * //     ParticipantAssignment: '1-2'  // Room 1 passengers (hyphen-separated)
 * //   },
 * //   {
 * //     Requirement: 'H',
 * //     Service: 'HERIRINI119',
 * //     Accommodation: 'D01H',
 * //     Amount: 1,
 * //     From: '/Date(...)/',
 * //     Till: '/Date(...)/',
 * //     ParticipantAssignment: '3'   // Room 2 passengers
 * //   }
 * // ]
 */
export function createMultiRoomServices(
  combination: RoomCombination,
  productCode: string
): DaVinciBookingService[] {
  const startDate = toUtcMidnight(combination.startDate);
  const endDate = toUtcMidnight(combination.endDate);

  return combination.products.map((product: MappedOffer): DaVinciBookingService => {
    const participantAssignment = createParticipantAssignmentForIds(product.passengerIds);

    return {
      Requirement: 'H', // Hotel requirement
      Service: productCode,
      Accommodation: `${product.roomCode}${product.boardCode}`, // e.g., "D03H"
      Amount: 1,
      From: formatDateForDaVinci(startDate),
      Till: formatDateForDaVinci(endDate),
      ParticipantAssignment: participantAssignment,
    };
  });
}

/**
 * Selected PPS extra paired with the user-picked ServiceType variant.
 *
 * Used as input for createAuxiliaryServices() — the caller resolves
 * selectedExtrasMap entries into (PpsService, serviceTypeCode) pairs.
 */
export interface PpsExtraSelection {
  service: PpsService;
  serviceTypeCode: string;
}

/**
 * Build DaVinci booking services for selected auxiliary products (insurance + extras).
 *
 * Produces the additional Services[] entries that get appended alongside the hotel
 * services in the second BA call (BA #2). DaVinci itself enriches these minimal
 * payloads with PriceInfo, Flags, __type etc. on the response.
 *
 * Participant assignment spans all participants (`1-N`) for both insurance and extras,
 * since SSC/PPS data is package-level (no per-room indexing in the API).
 *
 * @param params.selectedInsurance - Selected SSC insurance (or null if none)
 * @param params.selectedExtras - Selected PPS extras with their chosen ServiceType code
 * @param params.checkIn - Check-in date (used as default From for extras without own dates)
 * @param params.checkOut - Check-out date (used as default Till for extras without own dates)
 * @param params.participants - All participants in sequential room order (1..N).
 * @param params.passengerCounts - Passengers per room, e.g. [2, 1] means room 1 has
 *   participants 1-2, room 2 has participant 3. Used to split insurance into one
 *   entry per room (BA rejects a single multi-room insurance entry with 332).
 *   Extras remain package-level (no per-room split).
 * @returns Array of DaVinci booking service entries (empty if nothing selected)
 */
export function createAuxiliaryServices(params: {
  selectedInsurance: SscInsurance | null;
  selectedExtras: PpsExtraSelection[];
  checkIn: Date;
  checkOut: Date;
  participants: Array<{ type: 'adult' | 'child'; hotelClass?: 'adult' | 'child' | 'infant' }>;
  passengerCounts: number[];
}): DaVinciBookingService[] {
  const { selectedInsurance, selectedExtras, checkIn, checkOut, participants, passengerCounts } =
    params;

  const services: DaVinciBookingService[] = [];
  const from = formatDateForDaVinci(checkIn);
  const till = formatDateForDaVinci(checkOut);
  const totalParticipants = participants.length;
  const allParticipantIds = Array.from({ length: totalParticipants }, (_, i) => i + 1);
  const participantAssignment = createParticipantAssignmentForIds(allParticipantIds);

  // Insurance service — one entry per room (UI enforces single insurance product selection).
  // Multi-room bookings require per-room insurance entries; a single package-level entry
  // is rejected by BA with ReturnCode 332.
  if (selectedInsurance && selectedInsurance.ServiceTypes.length > 0) {
    const serviceType = selectedInsurance.ServiceTypes[0];
    let cursor = 0;
    for (const roomSize of passengerCounts) {
      const roomParticipants = participants.slice(cursor, cursor + roomSize);
      const roomIds = Array.from({ length: roomSize }, (_, i) => cursor + i + 1);
      // Count on hotelClass (fallback to binary type for plain URLs): a hotel-adult
      // counts as adult even when under 18, and an infant counts as a child.
      // TODO: re-test infant insurance counts once insurance is re-enabled.
      const roomAdults = roomParticipants.filter((p) => effectiveHotelClass(p) === 'adult').length;
      const roomChildren = roomParticipants.filter(
        (p) => effectiveHotelClass(p) !== 'adult'
      ).length;

      services.push({
        Requirement: selectedInsurance.Requirement, // "V"
        Service: selectedInsurance.BookingCode, // e.g. "VERGFPS"
        Accommodation: serviceType.Code, // e.g. "VS"
        Amount: calculateInsuranceAmount(serviceType, {
          adults: roomAdults,
          children: roomChildren,
        }),
        From: from,
        Till: till,
        ParticipantAssignment: createParticipantAssignmentForIds(roomIds),
      });
      cursor += roomSize;
    }
  }

  // Extras (zero or more — one ServiceType variant per BookingCode)
  for (const { service, serviceTypeCode } of selectedExtras) {
    services.push({
      Requirement: service.Requirement, // "SO", "T", ...
      Service: service.BookingCode, // e.g. "GR01192"
      Accommodation: serviceTypeCode, // e.g. "TYP A"
      Amount: 1, // One unit per selected extra (pending official example)
      From: service.From ?? from,
      Till: service.To ?? till,
      ParticipantAssignment: participantAssignment,
    });
  }

  return services;
}

/**
 * Parse DD.MM.YYYY date string to DaVinci format
 *
 * @param dateString - Date in DD.MM.YYYY format
 * @returns DaVinci .NET date format or null if invalid
 */
function parseDDMMYYYYToDaVinci(dateString: string): string | null {
  const date = parseGermanDateUtc(dateString);
  return date ? formatDateForDaVinci(date) : null;
}

/**
 * Map form salutation to DaVinci Salutation + Gender pair.
 *
 * Bewotec requires both fields on every adult participant in B/BQ/BC and they
 * are kept in one mapper so the two values can never drift apart. The empty
 * Salutation originally proposed for "Diverse" makes Bewotec drop the
 * participant from its count (ReturnCode 333), so as an interim we send "D"
 * with Gender 0 — booking succeeds and Gender 0 still carries the
 * "unspecified" signal. Replace once Bewotec confirms an official code.
 */
function mapSalutationToDaVinci(salutation?: 'mr' | 'mrs' | 'diverse'): {
  Salutation: string;
  Gender: number;
} {
  switch (salutation) {
    case 'mr':
      return { Salutation: 'H', Gender: 0 };
    case 'mrs':
      return { Salutation: 'D', Gender: 1 };
    case 'diverse':
      return { Salutation: 'D', Gender: 0 };
    default:
      return { Salutation: 'H', Gender: 0 };
  }
}

/**
 * Map an under-18 hotel-adult's selected gender to a DaVinci Salutation + Gender pair.
 *
 * Unlike a hotel-classified child/infant (which keep the fixed 'K'/'B' marker), an
 * under-18 adult is an *adult* to the hotel, so it carries an adult salutation derived
 * from gender — mirroring mapSalutationToDaVinci but driven by the gender dropdown that
 * the gender-style form renders. Diverse → 'D'/0 (same interim handling as adults).
 */
function mapGenderToAdultSalutation(gender?: 'male' | 'female' | 'diverse'): {
  Salutation: string;
  Gender: number;
} {
  switch (gender) {
    case 'female':
      return { Salutation: 'D', Gender: 1 };
    case 'diverse':
      return { Salutation: 'D', Gender: 0 };
    case 'male':
    default:
      return { Salutation: 'H', Gender: 0 };
  }
}

/**
 * Map a child's selected gender to the DaVinci numeric Gender field.
 *
 * Children carry their own gender field (adults derive Gender from salutation).
 * Bewotec has no dedicated "diverse" Gender value, so diverse → 0 (unspecified),
 * matching how adult "diverse" is handled in mapSalutationToDaVinci. The child's
 * Salutation stays 'K' regardless (see createParticipantsFromTravelers).
 */
function mapGenderToDaVinci(gender?: 'male' | 'female' | 'diverse'): number {
  switch (gender) {
    case 'female':
      return 1;
    case 'male':
    case 'diverse':
    default:
      return 0;
  }
}

/**
 * Create DaVinci client from checkout form data
 *
 * Extracts contact person information and formats it for DaVinci API.
 *
 * @param formData - Checkout form data with contact person
 * @returns DaVinci client object
 *
 * @example
 * createClientFromFormData(checkoutFormData)
 * // Returns: {
 * //   EMail: 'john@example.com',
 * //   Name: 'John',
 * //   Surname: 'Doe',
 * //   Address: { Street: 'Main St 1', ZipCode: '12345', City: 'Berlin', Country: 'DE' }
 * // }
 */
export function createClientFromFormData(
  formData: CheckoutFormData | DeepReadonly<CheckoutFormData>
): DaVinciClient {
  const { contactPerson } = formData;

  return {
    EMail: contactPerson.email,
    Telephone: contactPerson.mobile || null,
    Name: contactPerson.firstName,
    Surname: contactPerson.lastName,
    Address: {
      Street: contactPerson.street,
      ZipCode: contactPerson.postalCode,
      City: contactPerson.location,
      Country: contactPerson.country,
    },
  };
}

/**
 * Create DaVinci participants from travelers summary
 *
 * Converts traveler information to DaVinci participant format with real names, birth dates,
 * and address information (for adult travelers).
 *
 * Address is included for adult travelers who have address data:
 * - If invoiceRecipient === 'solo': First traveler is contact person with their address
 * - If invoiceRecipient === 'accompany': Each adult traveler has their own address
 *
 * Children don't have address data - they only have firstName, lastName, birthDate.
 *
 * Both adults and children carry their date of birth as Age.BirthDate here (the birth date
 * is known once the data entry step is complete), so DaVinci derives the exact age at the
 * travel date. Children keep Salutation 'K' as the child marker. This differs from the
 * pre-data-entry availability check (createParticipantsFromInfo), which only has ages.
 *
 * @param travelers - Array of travelers from travelersSummary computed
 * @returns Array of DaVinci participant objects with real data
 *
 * @example
 * createParticipantsFromTravelers([
 *   { id: 'contact', type: 'adult', firstName: 'John', lastName: 'Doe', birthDate: '01.01.1990', salutation: 'mr', street: 'Main St 1', postalCode: '12345', location: 'Berlin', country: 'DE', ... },
 *   { id: 1, type: 'child', firstName: 'Jane', lastName: 'Doe', birthDate: '15.06.2015', gender: 'female' }
 * ])
 */
export function createParticipantsFromTravelers(travelers: Traveler[]): DaVinciParticipant[] {
  return travelers.map((traveler) => {
    // Gender-style variants (infant / child / under18adult) share the same minimal shape
    // — name, birth date, nationality — and differ only in Salutation + Gender:
    //   - infant       → literal 'B' / Gender 0 (case F: B accepted with no gender; the
    //                    rendered gender dropdown is UI-only and never reaches the wire)
    //   - child        → 'K' / gender (diverse → 0; 'K' marker stays regardless)
    //   - under18adult → adult salutation derived from gender (hotel sees an adult)
    if (traveler.type !== 'adult') {
      const birthDate = traveler.birthDate ? parseDDMMYYYYToDaVinci(traveler.birthDate) : null;

      let Salutation: string;
      let Gender: number;
      if (traveler.type === 'infant') {
        Salutation = 'B';
        Gender = 0;
      } else if (traveler.type === 'under18adult') {
        ({ Salutation, Gender } = mapGenderToAdultSalutation(traveler.gender));
      } else {
        Salutation = 'K';
        Gender = mapGenderToDaVinci(traveler.gender);
      }

      return {
        Salutation,
        Gender,
        Name: `${traveler.lastName}/${traveler.firstName}`,
        DoNotAutoCreateClient: true,
        Age: birthDate ? { BirthDate: birthDate } : undefined,
        Passport: buildPassport(traveler.nationality),
      };
    }

    const { Salutation, Gender } = mapSalutationToDaVinci(traveler.salutation);
    const birthDate = traveler.birthDate ? parseDDMMYYYYToDaVinci(traveler.birthDate) : null;

    const participant: DaVinciParticipant = {
      Salutation,
      Gender,
      Name: `${traveler.lastName}/${traveler.firstName}`,
      DoNotAutoCreateClient: true,
      Age: birthDate ? { BirthDate: birthDate } : undefined,
      Passport: buildPassport(traveler.nationality),
    };

    if (traveler.street && traveler.postalCode && traveler.location && traveler.country) {
      participant.Address = {
        Street: traveler.street,
        ZipCode: traveler.postalCode,
        City: traveler.location,
        Country: traveler.country,
      };
    }

    participant.EMail = traveler.email || null;
    participant.Telephone = traveler.mobile || null;

    return participant;
  });
}

/**
 * Create default delivery configuration for booking
 *
 * @returns DaVinci delivery object with default settings
 */
export function createDeliveryConfig(): Record<string, unknown> {
  return {
    DeliveryType: { Manual: false },
    PrintPool: { Manual: false },
    ProcessingPool: { Manual: false },
    DeliveryDate: { Manual: false },
    NoTransmitToCets: false,
    BlockCreationOfDocs: false,
    DoNotBookExternalFlights: false,
    DoNotBookExternalTrains: false,
  };
}

/**
 * Create payment configuration for booking
 *
 * Currently only supports bank transfer (PaymentType: 8).
 * Payment gateway integration planned for future implementation.
 *
 * @param paymentType - Payment type code (default: 8 for bank transfer)
 * @returns DaVinci payment object
 */
export function createPaymentConfig(
  paymentType: number = CHECKOUT_CONFIG.PAYMENT.DEFAULT_TYPE
): Record<string, unknown> {
  return {
    __type: 'BookingPaymentBankTransfer:http://bewotec.de/Action',
    PaymentProfile: { Value: 1, Manual: false },
    PaymentType: paymentType,
    PaymentAction: 0,
    PrintLetterOfBankTransfer: false,
  };
}
